import { isDefined } from '@sgme/fp';
import { UnderlyingInfo } from 'components/shared/UnderlyingInfo/UnderlyingInfo';
import { EsgEligibilityCell } from 'components/shared/underlyings/underlyingsGridLegacy/Table/Common/Cells/esg-eligibility';
import { UnderlyingTag } from 'components/underlying-tag';
import { useUnderlyingContext } from 'hooks/useUnderlyingContext';
import { StrikeMetadata } from 'models/forms/fields';
import type { EquityUnderlying } from 'models/underlying/equity';
import { FC } from 'react';
import { getUnderlyingName } from 'utils/underlying/underlying-code';

import { ActionCell, CodeCell, NameCell, StrikeCell, WeightCell } from '../../Common/Cells';
import { Row } from '../../Common/Row';
import { TableWithHiddenColumn } from '../../Common/Table';
import { useCodeTypeSwitcher } from '../../Table.hooks';

export interface EquityTableProps {
  underlyings: EquityUnderlying[];
  strikeMetadata: StrikeMetadata;
}

export const EquityTable: FC<EquityTableProps> = ({ underlyings, strikeMetadata }) => {
  const [currentCodeType, switchCurrentCodeType] = useCodeTypeSwitcher(underlyings);
  const { canBeRemoved, isReadOnly } = useUnderlyingContext();
  const showWeight = underlyings.some(x => isDefined(x.weight));
  const hasStrikes = underlyings.some(underlying => isDefined(underlying.strike));
  const HasEsgExtendedEligibility = underlyings.some(underlying => isDefined(underlying.esgEligibility));

  return (
    <>
      <TableWithHiddenColumn columnName="name">
        <thead>
          <tr>
            <CodeCell.Header type={currentCodeType} switchCurrentCodeType={switchCurrentCodeType} />
            <NameCell.Header enableResponsive />
            {HasEsgExtendedEligibility && <EsgEligibilityCell.Header />}
            <WeightCell.Header showWeight={showWeight} />
            {hasStrikes && <StrikeCell.Header strikeMetadata={strikeMetadata} />}
            <ActionCell.RemoveAll />
          </tr>
        </thead>
        <tbody>
          {underlyings.map(underlying => (
            <Row key={underlying.id}>
              <CodeCell.Data>
                {!!currentCodeType && (
                  <UnderlyingTag
                    dataE2e="selected-underlying-code"
                    label={getUnderlyingName(underlying, currentCodeType)}
                    value={underlying}
                    isReadOnly
                    size="lg"
                  />
                )}
                <UnderlyingInfo underlying={underlying} />
              </CodeCell.Data>
              <NameCell.Data underlying={underlying} enableResponsive maxWidth={220}>
                {underlying.name}
              </NameCell.Data>
              {HasEsgExtendedEligibility && (
                <EsgEligibilityCell.Data
                  isEligible={underlying.esgEligibility}
                  isEligibleWithCondition={underlying.esgExtendedEligibility}
                />
              )}
              <WeightCell.Data
                underlyingId={underlying.id}
                weightValue={underlying.weight}
                showWeight={showWeight}
                isWeightInPercent={underlying.isWeightInPercent}
              />
              {hasStrikes && (
                <>
                  {underlying.strike ? (
                    <StrikeCell.Data
                      {...underlying.strike}
                      currency={underlying.currency}
                      underlyingId={underlying.id}
                      calendar={underlying.calendar}
                      strikeMetadata={strikeMetadata}
                    />
                  ) : (
                    <StrikeCell.DataSkeleton />
                  )}
                </>
              )}
              {canBeRemoved && !isReadOnly && <ActionCell.Remove underlyingId={underlying.id} />}
            </Row>
          ))}
          <tr>
            <td colSpan={10}></td>
          </tr>
        </tbody>
      </TableWithHiddenColumn>
    </>
  );
};

EquityTable.displayName = 'EquityTable';
